import { useState, useEffect, useCallback, useRef } from 'react';
import { adminApi } from '../../lib/adminApi';
import { toast } from 'sonner';
import {
  MagnifyingGlass,
  Trash,
  ArrowLeft,
  Buildings,
  Warning,
  ArrowClockwise,
  Crown,
  Link as LinkIcon,
  CursorClick,
  UserPlus,
  CreditCard,
  PencilSimple,
  CaretDown,
  CheckCircle,
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Label } from '../../components/ui/label';

const MEMBER_ROLES = [
  { value: 'owner', label: 'Owner', hint: 'Full control + billing' },
  { value: 'admin', label: 'Admin', hint: 'Manage members + content' },
  { value: 'member', label: 'Member', hint: 'Create + edit content' },
  { value: 'viewer', label: 'Viewer', hint: 'Read-only access' },
];

const PAGE_LIMIT = 20;

function PlanBadge({ plan }) {
  const isPro = plan === 'pro';
  return (
    <span
      className={`chip ${
        isPro ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
      }`}
    >
      {isPro ? <Crown size={11} weight="fill" /> : null}
      {plan || 'free'}
    </span>
  );
}

function WorkspaceDetail({ workspaceId, onBack }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [memberActionLoading, setMemberActionLoading] = useState(null);
  const [removeMemberTarget, setRemoveMemberTarget] = useState(null);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [addMemberForm, setAddMemberForm] = useState({ email: '', role: 'member' });
  const [addMemberSaving, setAddMemberSaving] = useState(false);
  const [editPlanOpen, setEditPlanOpen] = useState(false);
  const [planForm, setPlanForm] = useState({ plan: 'free', plan_expires_at: '' });
  const [planSaving, setPlanSaving] = useState(false);

  const fetchDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);
      const res = await adminApi.getWorkspaceDetail(workspaceId);
      setDetail(res);
    } catch (err) {
      console.error('Failed to load workspace detail', err);
      setError(true);
      toast.error('Failed to load workspace detail');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleSetMemberRole = async (member, role) => {
    if (member.role === role) return;
    try {
      setMemberActionLoading(member.user_id);
      await adminApi.setWorkspaceMemberRole(workspaceId, member.user_id, role);
      toast.success(`${member.name || member.email} is now ${role}`);
      fetchDetail();
    } catch (err) {
      toast.error(err.message || 'Failed to update role');
    } finally {
      setMemberActionLoading(null);
    }
  };

  const handleRemoveMember = async () => {
    if (!removeMemberTarget) return;
    try {
      setMemberActionLoading(removeMemberTarget.user_id);
      await adminApi.removeWorkspaceMember(workspaceId, removeMemberTarget.user_id);
      toast.success('Member removed');
      setRemoveMemberTarget(null);
      fetchDetail();
    } catch (err) {
      toast.error(err.message || 'Failed to remove member');
    } finally {
      setMemberActionLoading(null);
    }
  };

  const handleAddMember = async (e) => {
    e?.preventDefault?.();
    const email = addMemberForm.email.trim();
    if (!email) {
      toast.error('Enter an email');
      return;
    }
    try {
      setAddMemberSaving(true);
      await adminApi.addWorkspaceMember(workspaceId, {
        email,
        role: addMemberForm.role,
      });
      toast.success(`${email} added as ${addMemberForm.role}`);
      setAddMemberOpen(false);
      setAddMemberForm({ email: '', role: 'member' });
      fetchDetail();
    } catch (err) {
      toast.error(err.message || 'Failed to add member');
    } finally {
      setAddMemberSaving(false);
    }
  };

  const openEditPlan = () => {
    const ws = detail?.workspace || {};
    setPlanForm({
      plan: ws.plan || 'free',
      plan_expires_at: ws.plan_expires_at
        ? new Date(ws.plan_expires_at).toISOString().slice(0, 10)
        : '',
    });
    setEditPlanOpen(true);
  };

  const handleSavePlan = async (e) => {
    e?.preventDefault?.();
    try {
      setPlanSaving(true);
      const payload = { plan: planForm.plan };
      if (planForm.plan === 'pro' && planForm.plan_expires_at) {
        // Treat the date as end-of-day UTC so "today" doesn't expire mid-day.
        payload.plan_expires_at = new Date(
          `${planForm.plan_expires_at}T23:59:59.000Z`,
        ).toISOString();
      }
      await adminApi.setWorkspacePlan(workspaceId, payload);
      toast.success(
        planForm.plan === 'pro' ? 'Workspace upgraded to Pro' : 'Workspace set to Free',
      );
      setEditPlanOpen(false);
      fetchDetail();
    } catch (err) {
      toast.error(err.message || 'Failed to update plan');
    } finally {
      setPlanSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-10 animate-fade-in max-w-[1280px] mx-auto w-full">
        <div className="h-8 w-32 bg-muted rounded-xl animate-pulse mb-6" />
        <div className="h-24 surface-card animate-pulse mb-6" />
        <div className="h-72 surface-card animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 md:p-10 animate-fade-in">
        <div className="surface-card p-12 flex flex-col items-center justify-center text-center max-w-2xl mx-auto">
          <div className="grid place-items-center h-14 w-14 rounded-2xl bg-destructive/10 text-destructive mb-4">
            <Warning size={26} weight="fill" />
          </div>
          <h2 className="font-display text-2xl font-extrabold text-foreground mb-2">
            Failed to load workspace detail
          </h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-md">
            We couldn't fetch the workspace details. Check your connection and try again.
          </p>
          <div className="flex gap-2.5">
            <Button
              onClick={onBack}
              variant="outline"
              className="rounded-xl gap-2 px-5 h-11"
            >
              <ArrowLeft size={16} weight="bold" />
              Back
            </Button>
            <Button
              onClick={fetchDetail}
              className="rounded-xl gradient-primary text-white hover:opacity-95 px-5 h-11 font-semibold gap-2"
            >
              <ArrowClockwise size={16} weight="bold" />
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const workspace = detail?.workspace || {};
  const members = detail?.members || [];
  const links = detail?.links || [];

  return (
    <div className="p-6 md:p-10 animate-fade-in max-w-[1280px] mx-auto w-full" data-testid="workspace-detail">
      <Button
        onClick={onBack}
        variant="ghost"
        className="mb-4 rounded-lg hover:bg-muted gap-2 px-2 -ml-2 text-muted-foreground hover:text-foreground"
        data-testid="back-btn"
      >
        <ArrowLeft size={16} weight="bold" />
        Back to workspaces
      </Button>

      {/* Workspace info */}
      <div className="surface-card p-6 mb-6 ambient-shadow">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <span className="grid place-items-center h-12 w-12 rounded-xl bg-primary/10 text-primary text-base font-bold shrink-0">
              {(workspace.name || 'W')[0]?.toUpperCase()}
            </span>
            <div className="min-w-0">
              <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground leading-tight truncate">
                {workspace.name || 'Workspace'}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-muted-foreground">
                <span>
                  Slug:{' '}
                  <span className="font-mono font-medium text-foreground">
                    {workspace.slug || '—'}
                  </span>
                </span>
                <span className="text-border">·</span>
                <PlanBadge plan={workspace.plan} />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-border/70 bg-muted/30 px-4 py-2.5 min-w-[120px]">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground flex items-center gap-1.5">
                <LinkIcon size={11} weight="fill" />
                Links
              </p>
              <p className="font-display text-xl font-extrabold text-foreground tabular-nums">
                {links.length.toLocaleString()}
              </p>
            </div>
            <div className="rounded-xl border border-border/70 bg-muted/30 px-4 py-2.5 min-w-[120px]">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground flex items-center gap-1.5">
                <CursorClick size={11} weight="fill" />
                Members
              </p>
              <p className="font-display text-xl font-extrabold text-foreground tabular-nums">
                {members.length.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Billing / Plan */}
      <div className="mb-6">
        <h2 className="font-display text-lg font-bold text-foreground mb-3 px-1">
          Billing & plan
        </h2>
        <div className="surface-card p-6 ambient-shadow">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-start gap-4">
              <span className="grid place-items-center h-12 w-12 rounded-xl bg-emerald-100 text-emerald-600 shrink-0">
                <CreditCard size={20} weight="fill" />
              </span>
              <div>
                <div className="flex items-center gap-2">
                  <PlanBadge plan={workspace.plan} />
                  {workspace.plan_expires_at ? (
                    <span className="text-xs text-muted-foreground">
                      until{' '}
                      <span className="font-medium text-foreground">
                        {new Date(workspace.plan_expires_at).toLocaleDateString()}
                      </span>
                    </span>
                  ) : workspace.plan === 'pro' ? (
                    <span className="text-xs text-muted-foreground">no expiry</span>
                  ) : null}
                </div>
                <div className="mt-2 text-xs text-muted-foreground space-y-1">
                  <p>
                    Stripe customer:{' '}
                    <span className="font-mono text-foreground">
                      {workspace.stripe_customer_id || '—'}
                    </span>
                  </p>
                  {workspace.plan_updated_at && (
                    <p>
                      Last changed:{' '}
                      <span className="font-medium text-foreground">
                        {new Date(workspace.plan_updated_at).toLocaleString()}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            </div>
            <Button
              onClick={openEditPlan}
              variant="outline"
              className="rounded-xl border-border bg-white h-10 px-4 gap-2 self-start"
              data-testid="edit-plan-btn"
            >
              <PencilSimple size={14} weight="bold" />
              Change plan
            </Button>
          </div>
        </div>
      </div>

      {/* Members */}
      <div className="mb-6">
        <div className="flex items-end justify-between gap-3 mb-3 px-1">
          <h2 className="font-display text-lg font-bold text-foreground">
            Members ({members.length})
          </h2>
          <Button
            onClick={() => setAddMemberOpen(true)}
            className="rounded-xl gradient-primary text-white hover:opacity-95 h-10 px-4 font-semibold gap-2 ambient-shadow"
            data-testid="add-member-btn"
          >
            <UserPlus size={14} weight="bold" />
            Add member
          </Button>
        </div>
        <div className="surface-card overflow-hidden ambient-shadow">
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-modern">
              <thead>
                <tr>
                  <th className="text-left">Name</th>
                  <th className="text-left">Email</th>
                  <th className="text-left">Role</th>
                  <th className="text-left">Joined</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-12 text-sm text-muted-foreground">
                      No members found.
                    </td>
                  </tr>
                ) : (
                  members.map((member, idx) => {
                    const isOwner = workspace.owner_id === member.user_id;
                    const role = member.role || 'member';
                    return (
                      <tr key={member.user_id || idx} className="link-row">
                        <td className="font-semibold text-foreground">
                          {member.name || '—'}
                        </td>
                        <td className="text-muted-foreground">{member.email || '—'}</td>
                        <td>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                disabled={isOwner || memberActionLoading === member.user_id}
                                className={`chip transition hover:opacity-90 ${
                                  role === 'owner'
                                    ? 'bg-primary/10 text-primary border border-primary/15'
                                    : 'bg-muted text-muted-foreground border border-border/70'
                                } ${isOwner ? 'cursor-not-allowed' : ''}`}
                                data-testid={`member-role-trigger-${member.user_id}`}
                              >
                                {role === 'owner' ? <Crown size={11} weight="fill" /> : null}
                                {role}
                                {!isOwner && <CaretDown size={10} weight="bold" className="opacity-70" />}
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-[240px]">
                              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                                Workspace role
                              </DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              {MEMBER_ROLES.map((r) => (
                                <DropdownMenuItem
                                  key={r.value}
                                  disabled={role === r.value || (r.value === 'owner' && !isOwner)}
                                  onSelect={() => handleSetMemberRole(member, r.value)}
                                  data-testid={`set-member-role-${member.user_id}-${r.value}`}
                                >
                                  {r.value === 'owner' ? (
                                    <Crown size={14} className="mr-2 text-primary" />
                                  ) : (
                                    <span className="w-[14px] mr-2" />
                                  )}
                                  <div className="flex-1">
                                    <p className="text-sm font-medium">{r.label}</p>
                                    <p className="text-[11px] text-muted-foreground">{r.hint}</p>
                                  </div>
                                  {role === r.value && (
                                    <CheckCircle size={14} className="text-emerald-600" />
                                  )}
                                </DropdownMenuItem>
                              ))}
                              {isOwner && (
                                <>
                                  <DropdownMenuSeparator />
                                  <div className="px-2 py-1.5 text-[11px] text-muted-foreground">
                                    Owner role can't be changed here.
                                  </div>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                        <td className="text-muted-foreground">
                          {member.joined_at
                            ? new Date(member.joined_at).toLocaleDateString()
                            : '—'}
                        </td>
                        <td className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setRemoveMemberTarget(member)}
                            disabled={isOwner || memberActionLoading === member.user_id}
                            className="h-8 w-8 p-0 rounded-lg hover:bg-destructive/10"
                            title={isOwner ? 'Owner can\'t be removed' : 'Remove member'}
                            data-testid={`remove-member-${member.user_id}`}
                          >
                            <Trash size={16} className={isOwner ? 'text-muted-foreground/40' : 'text-destructive'} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Links count */}
      <div className="surface-card p-6 ambient-shadow">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-bold text-foreground">Links</h2>
            <p className="text-sm text-muted-foreground">
              {links.length.toLocaleString()} link{links.length !== 1 ? 's' : ''} in this workspace
            </p>
          </div>
          <span className="grid place-items-center h-12 w-12 rounded-xl bg-amber-100 text-amber-600">
            <LinkIcon size={20} weight="fill" />
          </span>
        </div>
      </div>

      {/* Add member dialog */}
      <Dialog open={addMemberOpen} onOpenChange={setAddMemberOpen}>
        <DialogContent className="rounded-2xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add member to workspace</DialogTitle>
            <DialogDescription>
              The user must already have a LinkFlow account. We'll match them by email.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddMember} className="space-y-4 pt-2">
            <div>
              <Label htmlFor="member-email" className="text-sm font-semibold text-foreground">
                Email
              </Label>
              <input
                id="member-email"
                type="email"
                required
                autoFocus
                value={addMemberForm.email}
                onChange={(e) => setAddMemberForm((p) => ({ ...p, email: e.target.value }))}
                className="input-modern mt-1.5"
                placeholder="user@example.com"
                data-testid="add-member-email"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-foreground">Role</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                {MEMBER_ROLES.filter((r) => r.value !== 'owner').map((r) => {
                  const active = addMemberForm.role === r.value;
                  return (
                    <button
                      type="button"
                      key={r.value}
                      onClick={() => setAddMemberForm((p) => ({ ...p, role: r.value }))}
                      className={`text-left rounded-xl border px-3 py-2.5 transition ${
                        active
                          ? 'border-primary/40 bg-primary/5 shadow-[0_8px_22px_-12px_rgba(70,72,212,0.35)]'
                          : 'border-border/70 bg-white hover:border-border'
                      }`}
                      data-testid={`add-member-role-${r.value}`}
                    >
                      <p className={`text-sm font-semibold ${active ? 'text-primary' : 'text-foreground'}`}>
                        {r.label}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{r.hint}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setAddMemberOpen(false)}
                className="rounded-xl"
                disabled={addMemberSaving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={addMemberSaving}
                className="rounded-xl gradient-primary text-white hover:opacity-95 font-semibold gap-2"
                data-testid="confirm-add-member"
              >
                <UserPlus size={14} weight="bold" />
                {addMemberSaving ? 'Adding…' : 'Add member'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit plan dialog */}
      <Dialog open={editPlanOpen} onOpenChange={setEditPlanOpen}>
        <DialogContent className="rounded-2xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change billing plan</DialogTitle>
            <DialogDescription>
              Manual override. Stripe is not contacted; this only changes what
              feature gates the app enforces for this workspace.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSavePlan} className="space-y-4 pt-2">
            <div>
              <Label className="text-sm font-semibold text-foreground">Plan</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                {[
                  { value: 'free', label: 'Free', hint: 'Basic limits apply' },
                  { value: 'pro', label: 'Pro', hint: 'All paid features' },
                ].map((p) => {
                  const active = planForm.plan === p.value;
                  return (
                    <button
                      type="button"
                      key={p.value}
                      onClick={() => setPlanForm((prev) => ({ ...prev, plan: p.value }))}
                      className={`text-left rounded-xl border px-3 py-3 transition ${
                        active
                          ? 'border-primary/40 bg-primary/5 shadow-[0_8px_22px_-12px_rgba(70,72,212,0.35)]'
                          : 'border-border/70 bg-white hover:border-border'
                      }`}
                      data-testid={`plan-option-${p.value}`}
                    >
                      <div className="flex items-center gap-2">
                        {p.value === 'pro' && <Crown size={14} weight="fill" className="text-primary" />}
                        <span className={`text-sm font-bold ${active ? 'text-primary' : 'text-foreground'}`}>
                          {p.label}
                        </span>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{p.hint}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            {planForm.plan === 'pro' && (
              <div>
                <Label htmlFor="plan-expires" className="text-sm font-semibold text-foreground">
                  Expires on (optional)
                </Label>
                <p className="text-[11px] text-muted-foreground mt-1 mb-1.5">
                  After this date the workspace will revert to Free at app level. Leave blank for no expiry.
                </p>
                <input
                  id="plan-expires"
                  type="date"
                  value={planForm.plan_expires_at}
                  onChange={(e) => setPlanForm((p) => ({ ...p, plan_expires_at: e.target.value }))}
                  className="input-modern"
                  data-testid="plan-expiry-input"
                />
              </div>
            )}
            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditPlanOpen(false)}
                className="rounded-xl"
                disabled={planSaving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={planSaving}
                className="rounded-xl gradient-primary text-white hover:opacity-95 font-semibold gap-2"
                data-testid="confirm-save-plan"
              >
                <CreditCard size={14} weight="bold" />
                {planSaving ? 'Saving…' : 'Save plan'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Remove member confirm */}
      <AlertDialog
        open={!!removeMemberTarget}
        onOpenChange={(open) => !open && setRemoveMemberTarget(null)}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove member</AlertDialogTitle>
            <AlertDialogDescription>
              Remove{' '}
              <span className="font-semibold text-foreground">
                {removeMemberTarget?.name || removeMemberTarget?.email}
              </span>{' '}
              from <span className="font-semibold text-foreground">{workspace.name}</span>?
              They'll lose access to this workspace's links and analytics. The user account
              itself isn't affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-xl"
              data-testid="confirm-remove-member"
            >
              Remove member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function AdminWorkspaces() {
  const [workspaces, setWorkspaces] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null);
  const debounceRef = useRef(null);

  const fetchWorkspaces = useCallback(async (currentPage, currentSearch) => {
    try {
      setLoading(true);
      setError(false);
      const params = { page: currentPage, limit: PAGE_LIMIT };
      if (currentSearch) params.search = currentSearch;
      const res = await adminApi.getWorkspaces(params);
      setWorkspaces(res.items || []);
      setTotal(res.total || 0);
    } catch (err) {
      console.error('Failed to load workspaces', err);
      setError(true);
      toast.error('Failed to load workspaces');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkspaces(page, search);
  }, [fetchWorkspaces, page, search]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(value);
      setPage(1);
    }, 300);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setActionLoading(deleteTarget.workspace_id);
      await adminApi.deleteWorkspace(deleteTarget.workspace_id);
      toast.success('Workspace deleted successfully');
      setDeleteTarget(null);
      fetchWorkspaces(page, search);
    } catch (err) {
      toast.error(err.message || 'Failed to delete workspace');
    } finally {
      setActionLoading(null);
    }
  };

  const totalPages = Math.ceil(total / PAGE_LIMIT);

  if (selectedWorkspaceId) {
    return (
      <WorkspaceDetail
        workspaceId={selectedWorkspaceId}
        onBack={() => setSelectedWorkspaceId(null)}
      />
    );
  }

  if (loading && workspaces.length === 0) {
    return (
      <div className="p-6 md:p-10 animate-fade-in max-w-[1280px] mx-auto w-full">
        <div className="h-9 w-56 bg-muted rounded-xl animate-pulse mb-6" />
        <div className="h-12 w-80 bg-muted rounded-xl animate-pulse mb-6" />
        <div className="surface-card overflow-hidden">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-16 border-t border-border/70 animate-pulse first:border-t-0" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 md:p-10 animate-fade-in" data-testid="admin-workspaces-error">
        <div className="surface-card p-12 flex flex-col items-center justify-center text-center max-w-2xl mx-auto">
          <div className="grid place-items-center h-14 w-14 rounded-2xl bg-destructive/10 text-destructive mb-4">
            <Warning size={26} weight="fill" />
          </div>
          <h2 className="font-display text-2xl font-extrabold text-foreground mb-2">
            Failed to load workspaces
          </h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-md">
            We couldn't fetch the workspace list. Check your connection and try again.
          </p>
          <Button
            onClick={() => fetchWorkspaces(page, search)}
            data-testid="retry-btn"
            className="rounded-xl gradient-primary text-white hover:opacity-95 px-6 py-2.5 font-semibold gap-2"
          >
            <ArrowClockwise size={16} weight="bold" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 animate-fade-in max-w-[1280px] mx-auto w-full" data-testid="admin-workspaces-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-[2.5rem] font-extrabold tracking-tight text-foreground leading-tight">
            Workspaces
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            <span className="font-medium text-foreground/90">{total}</span> total workspace
            {total !== 1 ? 's' : ''} across the platform.
          </p>
        </div>
        <div className="relative w-full sm:w-80">
          <MagnifyingGlass
            size={16}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
          />
          <input
            type="search"
            placeholder="Search by name or slug…"
            onChange={handleSearchChange}
            className="input-modern pl-10"
            data-testid="search-input"
          />
        </div>
      </div>

      <div className="surface-card overflow-hidden ambient-shadow">
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-modern">
            <thead>
              <tr>
                <th className="text-left">Workspace</th>
                <th className="text-left">Owner</th>
                <th className="text-right">Links</th>
                <th className="text-right">Clicks</th>
                <th className="text-left">Plan</th>
                <th className="text-left">Created</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {workspaces.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-16">
                    <div className="flex flex-col items-center text-muted-foreground">
                      <Buildings size={32} weight="duotone" className="mb-2 text-muted-foreground/70" />
                      <span className="text-sm">
                        {search ? 'No workspaces match your search.' : 'No workspaces found.'}
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                workspaces.map((ws) => (
                  <tr
                    key={ws.workspace_id}
                    className="link-row cursor-pointer"
                    onClick={() => setSelectedWorkspaceId(ws.workspace_id)}
                    data-testid={`workspace-row-${ws.workspace_id}`}
                  >
                    <td>
                      <div className="flex items-center gap-3">
                        <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary/10 text-primary text-xs font-bold shrink-0">
                          {(ws.name || 'W')[0]?.toUpperCase()}
                        </span>
                        <div className="min-w-0">
                          <p className="font-semibold text-foreground truncate">
                            {ws.name || '—'}
                          </p>
                          <p className="text-xs font-mono text-muted-foreground truncate">
                            /{ws.slug || ws.workspace_id}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="text-muted-foreground truncate max-w-[220px]">
                      {ws.owner_email || '—'}
                    </td>
                    <td className="text-right font-semibold text-foreground tabular-nums">
                      {(ws.link_count ?? 0).toLocaleString()}
                    </td>
                    <td className="text-right font-semibold text-foreground tabular-nums">
                      {(ws.total_clicks ?? 0).toLocaleString()}
                    </td>
                    <td>
                      <PlanBadge plan={ws.plan} />
                    </td>
                    <td className="text-muted-foreground">
                      {ws.created_at
                        ? new Date(ws.created_at).toLocaleDateString()
                        : '—'}
                    </td>
                    <td className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(ws);
                        }}
                        disabled={actionLoading === ws.workspace_id}
                        className="h-8 w-8 p-0 rounded-lg hover:bg-destructive/10"
                        title="Delete workspace"
                        data-testid={`delete-${ws.workspace_id}`}
                      >
                        <Trash size={16} className="text-destructive" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border/70 bg-muted/20">
            <p className="text-xs text-muted-foreground">
              Page <span className="font-semibold text-foreground">{page}</span> of {totalPages}
              <span className="text-muted-foreground/70"> · {total} workspaces</span>
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="rounded-lg text-xs h-8 px-3"
                data-testid="prev-page"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                className="rounded-lg text-xs h-8 px-3"
                data-testid="next-page"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete workspace</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-semibold text-foreground">{deleteTarget?.name}</span>?
              This will remove the workspace and all its links, members, and click data.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-xl"
              data-testid="confirm-delete"
            >
              Delete workspace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
